import React, { useState } from 'react';
import './OfferLetterTemplates.css';

const OfferLetterTemplates = ({ onSelectTemplate, onClose, application }) => {
  const [templates, setTemplates] = useState([
    {
      id: 1,
      name: "Standard Corporate",
      description: "Professional corporate format with standard terms",
      category: "Corporate",
      color: "#2c5aa0",
      isSelected: false,
      content: {
        headerStyle: "corporate",
        sections: ["company", "offer", "compensation", "terms"],
        signatureStyle: "formal",
        previewContent: `
          <div class="template-preview corporate">
            <div class="preview-header">
              <div class="company-logo-placeholder"></div>
              <div class="company-name">${application?.company || 'Company Name'}</div>
            </div>
            <div class="preview-title">APPOINTMENT LETTER</div>
            <div class="preview-content">
              <p>Dear ${application?.name || '[Candidate Name]'},</p>
              <p>We are pleased to offer you the position of <strong>${application?.jobTitle || '[Position]'}</strong>.</p>
              <p><strong>Salary:</strong> [Salary Details]</p>
              <p><strong>Location:</strong> [Work Location]</p>
              <p><strong>Joining Date:</strong> [Date]</p>
            </div>
          </div>
        `
      }
    },
    {
      id: 2,
      name: "Tech Startup",
      description: "Modern startup format with flexible terms",
      category: "Startup",
      color: "#ff6b6b",
      isSelected: false,
      content: {
        headerStyle: "modern",
        sections: ["welcome", "role", "benefits", "culture", "signature"],
        signatureStyle: "modern",
        previewContent: `
          <div class="template-preview startup">
            <div class="preview-header-modern">
              <div class="company-name-modern">🚀 ${application?.company || 'Tech Company'}</div>
              <div class="tagline">Welcome Aboard!</div>
            </div>
            <div class="preview-content-modern">
              <p>Hi ${application?.name || '[Candidate]'},</p>
              <p>We're excited to offer you the <strong>${application?.jobTitle || 'Position'}</strong> role!</p>
              <div class="modern-details">
                <div>💼 Position: ${application?.jobTitle || 'Your Role'}</div>
                <div>📍 Location: [Flexible/Remote]</div>
                <div>📅 Start Date: [Choose your date]</div>
              </div>
            </div>
          </div>
        `
      }
    },
    {
      id: 3,
      name: "Formal Legal",
      description: "Traditional format with detailed legal terms",
      category: "Legal",
      color: "#4a5568",
      isSelected: false,
      content: {
        headerStyle: "traditional",
        sections: ["company", "offer", "terms", "confidentiality", "legal", "signature"],
        signatureStyle: "legal",
        previewContent: `
          <div class="template-preview legal">
            <div class="preview-header-legal">
              <div class="legal-header">
                <strong>${application?.company || 'Company Legal Name'}</strong>
                <div>Appointment Letter</div>
              </div>
              <div class="ref-number">Ref: HR/2024/${application?.id?.slice(-4) || '0001'}</div>
            </div>
            <div class="preview-content-legal">
              <p><strong>To:</strong> ${application?.name || '[Candidate Full Name]'}</p>
              <p>This letter constitutes a formal offer of employment...</p>
              <div class="legal-notes">
                <p>• All company policies apply</p>
                <p>• Confidentiality agreement included</p>
                <p>• Legal jurisdiction specified</p>
              </div>
            </div>
          </div>
        `
      }
    },
    {
      id: 4,
      name: "Creative Agency",
      description: "Creative format for design/marketing roles",
      category: "Creative",
      color: "#9f7aea",
      isSelected: false,
      content: {
        headerStyle: "creative",
        sections: ["welcome", "role", "compensation", "benefits", "signature"],
        signatureStyle: "casual",
        previewContent: `
          <div class="template-preview creative">
            <div class="preview-header-creative">
              <div class="creative-title">✨ Creative Offer ✨</div>
              <div class="creative-subtitle">${application?.company || 'Creative Agency'}</div>
            </div>
            <div class="preview-content-creative">
              <p>Hey ${application?.name?.split(' ')[0] || 'there'}! 👋</p>
              <p>We'd love for you to join us as our new</p>
              <div class="creative-role">${application?.jobTitle || 'Creative Role'}</div>
              <div class="creative-highlights">
                <div>🎨 Creative freedom</div>
                <div>🏡 Remote-friendly</div>
                <div>📈 Growth opportunities</div>
              </div>
            </div>
          </div>
        `
      }
    },
    {
      id: 5,
      name: "Internship Offer",
      description: "Simple format for internship positions",
      category: "Internship",
      color: "#38b2ac",
      isSelected: false,
      content: {
        headerStyle: "simple",
        sections: ["offer", "duration", "stipend", "terms", "signature"],
        signatureStyle: "simple",
        previewContent: `
          <div class="template-preview internship">
            <div class="preview-header-simple">
              <div class="internship-badge">INTERNSHIP</div>
              <div class="company-name-simple">${application?.company || 'Company Name'}</div>
            </div>
            <div class="preview-content-simple">
              <p>Dear ${application?.name || '[Student Name]'},</p>
              <p>We're pleased to offer you an internship position:</p>
              <div class="internship-details">
                <p><strong>Role:</strong> ${application?.jobTitle || 'Intern'}</p>
                <p><strong>Duration:</strong> [3-6 months]</p>
                <p><strong>Stipend:</strong> [Monthly amount]</p>
              </div>
            </div>
          </div>
        `
      }
    }
  ]);

  const [categories] = useState(["All", "Corporate", "Startup", "Legal", "Creative", "Internship"]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = selectedCategory === "All" || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
  };

  const handleUseTemplate = () => {
    if (selectedTemplateId) {
      const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
      onSelectTemplate(selectedTemplate);
    }
  };

  const getSelectedTemplate = () => {
    return templates.find(t => t.id === selectedTemplateId);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content templates-modal">
        <div className="modal-header">
          <h2>📋 Select Offer Letter Template</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Search and Filter Section */}
          <div className="template-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="🔍 Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="category-filters">
              {categories.map(category => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Templates Grid */}
          <div className="templates-grid">
            {filteredTemplates.map(template => (
              <div 
                key={template.id} 
                className={`template-card ${selectedTemplateId === template.id ? 'selected' : ''}`}
                onClick={() => handleTemplateSelect(template.id)}
                style={{ borderLeftColor: template.color }}
              >
                <div className="template-card-header">
                  <div className="template-name">{template.name}</div>
                  <div className="template-category" style={{ backgroundColor: template.color }}>
                    {template.category}
                  </div>
                </div>
                
                <div className="template-description">
                  {template.description}
                </div>

                {/* Live Preview Section */}
                <div 
                  className="template-preview-container"
                  dangerouslySetInnerHTML={{ __html: template.content.previewContent }}
                />

                <div className="template-features">
                  {template.content.sections.map((section, index) => (
                    <span key={index} className="feature-tag">
                      {section}
                    </span>
                  ))}
                </div>

                {selectedTemplateId === template.id && (
                  <div className="template-selected-indicator">
                    ✅ Selected
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Selected Template Preview */}
          {selectedTemplateId && (
            <div className="selected-template-preview">
              <div className="preview-header">
                <h3>🎯 Selected Template: {getSelectedTemplate()?.name}</h3>
                <div className="template-notes">
                  <p>This template will be loaded into the offer letter editor where you can:</p>
                  <ul>
                    <li>📝 Edit all content fields</li>
                    <li>🎨 Customize formatting</li>
                    <li>✍️ Add signatures</li>
                    <li>📧 Send to candidate</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="secondary-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="primary-btn" 
            onClick={handleUseTemplate}
            disabled={!selectedTemplateId}
          >
            🚀 Use This Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferLetterTemplates;